import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchReferralLink, type ReferralLink } from '../lib/referrals';
import { generateQRMatrix, qrToSVG } from '../lib/qr';
import './SharePearCard.css';
// The QR frame recipe lives with PairQR (white card, dark modules) — a code
// that will not scan is decoration, so both QR surfaces share one style.
import './PairQR.css';

/** Concierge voice for the two failure lines — Title Case, no exclamation. */
const LOAD_ERROR = 'Your Link Could Not Be Loaded — Try Again Later.';
const COPY_ERROR = 'Copy Did Not Work — Select The Link And Copy It By Hand.';
const SHARE_ERROR = 'Sharing Did Not Work — Copy The Link Instead.';

/** "1 friend has joined" vs "3 friends have joined" — and the days that are
 *  already vested with this couple. Counts only what the server calls
 *  `referred`: friends whose couples have FORMED. Nothing is promised twice. */
function vestedLine(referred: number, rewardDays: number): string {
  if (referred <= 0) {
    return 'No friends have joined yet — your days arrive as their couples form.';
  }
  const friends = referred === 1 ? '1 friend has joined' : `${referred} friends have joined`;
  const days = referred * rewardDays;
  return `${friends} — ${days} ${days === 1 ? 'day' : 'days'} of Plus are already with your couple.`;
}

/**
 * Share Pear — the referral card (card 3MWZ8Zs7jA7e, web half).
 *
 * The signed-in couple's own link, fetched from GET /api/referrals/link, with
 * Copy and Share beside it (same honesty rules as InviteCard: a cancelled
 * share sheet writes nothing, and a copy that did not happen never says it
 * did). The reward copy is exact about timing — the friend's 30 days land the
 * moment they redeem, this couple's 30 days vest when the friend's partner
 * joins — and the only number claimed as earned is the server's vested
 * `referred` count, never a hope.
 */
export function SharePearCard() {
  const [link, setLink] = useState<ReferralLink | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  /* Session-only dismiss, like the first act's Done: the card steps out of
     the stream for this visit and offers itself again on the next launch —
     it is an invitation, not a permanent resident above the thread. */
  const [dismissed, setDismissed] = useState(false);
  /* Scan-to-share QR (card NThh9EjYzupt) — the in-person path, collapsed by
     default so the card stays copy-first, exactly like the invite's PairQR. */
  const [qrOpen, setQrOpen] = useState(false);

  /** Every copy attempt takes a ticket (same rule as InviteCard): a slow
   *  write from an earlier tap can never confirm — or clear — a later one. */
  const attempt = useRef(0);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const r = await fetchReferralLink();
        if (alive) setLink(r);
      } catch (e) {
        console.debug('[Pear] Referral link load failed:', e);
        if (alive) setLoadError(LOAD_ERROR);
      }
    })();
    return () => {
      alive = false;
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    };
  }, []);

  /* Encoded from the SERVER's own link, never rebuilt here: the founder code
     and a couple's six-character code must scan to the same URL the Copy
     button hands out, or the two paths disagree about what was shared. */
  const qrSvg = useMemo(() => {
    if (!qrOpen || !link?.url) return null;
    try {
      return qrToSVG(generateQRMatrix(link.url), { size: 5, margin: 3 });
    } catch {
      return null; // past encoder capacity — hide rather than crash the card
    }
  }, [qrOpen, link?.url]);

  if (dismissed) return null;

  const founder = Boolean(link?.founder);
  const rewardDays = link?.rewardDays || 30;
  const url = link?.url ?? '';

  /** Legacy copy path: believe what execCommand answered — false is a miss. */
  const legacyCopy = (text: string): boolean => {
    if (typeof document.execCommand !== 'function') return false;
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand('copy') !== false;
    } catch {
      ok = false;
    }
    document.body.removeChild(ta);
    return ok;
  };

  const copyUrl = async () => {
    const mine = ++attempt.current;
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    setCopied(false); // a retry starts from zero — never a stale confirmation
    setCopyError(null);
    setShareError(null);
    let ok = false;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      ok = legacyCopy(url);
    }
    if (mine !== attempt.current) return; // superseded — stay silent
    if (!ok) {
      setCopyError(COPY_ERROR);
      return;
    }
    setCopied(true);
    copiedTimer.current = setTimeout(() => setCopied(false), 1500);
  };

  const shareUrl = async () => {
    setCopyError(null);
    setShareError(null);
    if (typeof navigator.share !== 'function') {
      // No share sheet here — copying the link is the closest honest equivalent.
      await copyUrl();
      return;
    }
    try {
      await navigator.share({
        title: 'Pear',
        text: link?.shareText ?? `Join me on Pear — ${url}`,
        url,
      });
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        return; // cancelled: no clipboard write behind the user's back
      }
      setShareError(SHARE_ERROR);
    }
  };

  return (
    <section className="pear-share-card" aria-label="Share Pear" data-test="share-pear-card">
      <div className="pear-share-card-head">
        <h2 className="pear-share-card-title">
          {founder
            ? 'Share Pear — Your Founder Code'
            : `Share Pear — You Both Get ${rewardDays} Days of Plus Free`}
        </h2>
        <button
          type="button"
          className="pear-share-card-dismiss"
          aria-label="Hide the Share Pear card"
          onClick={() => setDismissed(true)}
          data-test="share-pear-dismiss"
        >
          ✕
        </button>
      </div>

      <p className="pear-share-card-lede">
        {founder
          ? 'This is your founder code. Any couple who redeems it holds Pear Plus until 31 December 2099 — nothing to pay, and no days that run out.'
          : `Send your link to a couple you know. They get ${rewardDays} days of Pear Plus the moment they redeem it — added to the end of their free trial, or to the end of the period they have already paid for if they subscribe. Your ${rewardDays} days are added the moment their partner joins — that is when their couple forms.`}
      </p>

      {!founder && (
        <p className="pear-share-card-vested" data-test="share-pear-vested">
          {vestedLine(link?.referred ?? 0, rewardDays)}
        </p>
      )}

      {!link && !loadError && (
        <p className="pear-share-card-note" role="status">
          Getting your link…
        </p>
      )}

      {link && (
        <div className="pear-share-card-row">
          <code className="pear-share-card-url" data-test="share-pear-url">
            {link.url}
          </code>
          <button
            type="button"
            className={`pear-share-card-btn${copied ? ' pear-share-card-btn--copied' : ''}`}
            onClick={() => void copyUrl()}
            aria-label="Copy referral link"
            data-test="share-pear-copy"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            type="button"
            className="pear-share-card-btn"
            onClick={() => void shareUrl()}
            aria-label="Share referral link"
            data-test="share-pear-share"
          >
            Share
          </button>
          {/* Scan-to-share (card NThh9EjYzupt): the in-person path, beside the
              copy-first one — same generator the invite QR proved. */}
          <button
            type="button"
            className="pear-share-card-btn"
            onClick={() => setQrOpen(o => !o)}
            aria-expanded={qrOpen}
            aria-controls="share-pear-qr"
            aria-label={qrOpen ? 'Hide the share QR code' : 'Show the share QR code'}
            data-test="share-pear-qr-toggle"
          >
            {qrOpen ? 'Hide QR' : 'Show QR'}
          </button>
        </div>
      )}

      {qrSvg && (
        <div className="pear-qr" id="share-pear-qr">
          <div
            className="pear-qr-frame"
            role="img"
            aria-label="QR code — scan it to open this share link"
            data-test="share-pear-qr"
            /* eslint-disable-next-line react/no-danger -- self-generated SVG */
            dangerouslySetInnerHTML={{ __html: qrSvg }}
          />
        </div>
      )}

      {/* The annual half of the referral offer (card 627osN2a6fUY). Copy is
          Jonathan's, approved verbatim 2026-10-25 — wording stands as
          delivered, and the trigger it promises is annual-only in the worker.
          It sits BELOW the link row so the Copy/Share controls keep the
          position every walk and screenshot already assumes. */}
      {!founder && (
        <p className="pear-share-card-annual" data-test="share-pear-annual">
          The satisfaction of our couples is paramount, and recommending Pear is one of the finest
          validations of that. Should you refer a friend to Pear and they subscribe annually, you'll
          both receive an additional year of Pear Plus.
        </p>
      )}

      {copyError && (
        <p className="pear-share-card-error" role="alert" data-test="share-pear-copy-error">
          {copyError}
        </p>
      )}
      {shareError && (
        <p className="pear-share-card-error" role="alert" data-test="share-pear-share-error">
          {shareError}
        </p>
      )}
      {loadError && (
        <p className="pear-share-card-error" role="alert" data-test="share-pear-load-error">
          {loadError}
        </p>
      )}
    </section>
  );
}
